import { Link } from 'react-router-dom';
import { languages, defaultLanguage, collectionsBase } from '../config/languages';
import PageInfoStripe from '../components/PageInfoStripe';
import { useDocumentTitle } from '../hooks/useDocumentMeta';

// ─── Localised copy ────────────────────────────────────────────────────────────
// One entry per supported language; `codes` holds per-status-code title/message.
// Add a new status code here and it works everywhere ErrorPage is used — the
// layout, icon and actions are shared below.
const STRINGS = {
  en: {
    here: 'Error',
    home: 'Go to homepage',
    collections: 'Browse collections',
    retry: 'Try again',
    codes: {
      404: { title: 'Page not found', message: "The page you're looking for doesn't exist or may have moved." },
      503: { title: 'Service temporarily unavailable', message: 'We could not reach the service needed for this page. Please try again in a few minutes.' },
      500: { title: 'Something went wrong', message: 'An unexpected error occurred while loading this page.' },
      default: { title: 'Something went wrong', message: 'An unexpected error occurred.' },
    },
  },
  cy: {
    here: 'Gwall',
    home: "Yn ôl i'r hafan",
    collections: 'Pori casgliadau',
    retry: 'Ceisiwch eto',
    codes: {
      404: { title: 'Tudalen heb ei chanfod', message: "Nid yw'r dudalen rydych yn chwilio amdani yn bodoli neu efallai ei bod wedi symud." },
      503: { title: 'Gwasanaeth ddim ar gael dros dro', message: 'Ni allem gyrraedd y gwasanaeth sydd ei angen ar gyfer y dudalen hon. Ceisiwch eto mewn ychydig funudau.' },
      500: { title: "Aeth rhywbeth o'i le", message: "Digwyddodd gwall annisgwyl wrth lwytho'r dudalen hon." },
      default: { title: "Aeth rhywbeth o'i le", message: 'Digwyddodd gwall annisgwyl.' },
    },
  },
  gd: {
    here: 'Mearachd',
    home: 'Air ais dhan dachaigh',
    collections: 'Rùraich cruinneachaidhean',
    retry: 'Feuch a-rithist',
    codes: {
      404: { title: 'Cha deach an duilleag a lorg', message: "Chan eil an duilleag a tha thu a' sireadh ann, no dh'fhaodadh gun deach a gluasad." },
      503: { title: 'Chan eil an t-seirbheis ri làimh an-dràsta', message: "Cha b' urrainn dhuinn an t-seirbheis a tha a dhìth airson na duilleige seo a ruigsinn. Feuch a-rithist an ceann beagan mhionaidean." },
      500: { title: 'Chaidh rudeigin ceàrr', message: "Thachair mearachd ris nach robh dùil fhad 's a bha an duilleag seo a' luchdadh." },
      default: { title: 'Chaidh rudeigin ceàrr', message: 'Thachair mearachd ris nach robh dùil.' },
    },
  },
};

// Accent colour for the status number + icon, keyed by code.
const ACCENT = {
  404: 'text-accent-text',
  503: 'text-accent-warning',
  500: 'text-accent-danger',
  default: 'text-accent-text',
};

function ErrorIcon({ code, className }) {
  const common = { className, 'aria-hidden': true, focusable: 'false', fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', strokeWidth: 1.5 };
  if (code === 404) {
    // Map-pin with a slash — "location not found"
    return (
      <svg {...common}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    );
  }
  if (code === 503) {
    // Cloud with a slash — "service offline"
    return (
      <svg {...common}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
      </svg>
    );
  }
  // 500 / default — alert triangle
  return (
    <svg {...common}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

// ─── Error page ────────────────────────────────────────────────────────────────
// Reusable for any status code. `onRetry` adds a "Try again" button (used by
// 5xx pages); `detail` shows an optional technical line (e.g. the raw error).
export default function ErrorPage({ code = 404, langCode, onRetry, detail }) {
  const lang = languages[langCode] || languages[defaultLanguage];
  const t = STRINGS[lang.code] || STRINGS[defaultLanguage];
  const copy = t.codes[code] || t.codes.default;
  const accent = ACCENT[code] || ACCENT.default;
  useDocumentTitle(copy.title, lang.code);

  const breadcrumbs = [
    { label: lang.homeLabel, path: lang.urlPrefix },
    { label: t.here },
  ];

  const primaryBtn = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-700 text-white font-semibold shadow-sm hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-700 focus:ring-offset-2 transition-colors';
  const secondaryBtn = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-700 text-gray-700 dark:text-dark-200 font-semibold hover:border-accent-primary hover:text-accent-text focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 transition-colors';

  return (
    <>
      <PageInfoStripe breadcrumbs={breadcrumbs} />
      <main className="max-w-3xl mx-auto px-6 py-20 flex flex-col items-center text-center">
        <ErrorIcon code={code} className={`w-16 h-16 mb-4 ${accent}`} />
        <p className={`text-6xl md:text-7xl font-extrabold tracking-tight tabular-nums ${accent}`}>{code}</p>
        <h1 className="mt-4 text-2xl md:text-3xl font-bold text-gray-900 dark:text-dark-100">{copy.title}</h1>
        <p className="mt-3 text-gray-600 dark:text-dark-400 max-w-prose">{copy.message}</p>

        {detail && (
          <p className="mt-4 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-dark-400 font-mono text-xs break-all max-w-prose">
            {detail}
          </p>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to={lang.urlPrefix} className={primaryBtn}>
            <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
            </svg>
            {t.home}
          </Link>

          {code === 404 && (
            <Link to={collectionsBase(lang.code)} className={secondaryBtn}>
              {t.collections}
              <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          )}

          {onRetry && (
            <button type="button" onClick={onRetry} className={secondaryBtn}>
              <svg aria-hidden="true" focusable="false" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M4.018 19.644v-4.992h4.992M19.642 9.348a8.001 8.001 0 00-14.95-2.648M4.358 14.652a8.003 8.003 0 0014.95 2.648" />
              </svg>
              {t.retry}
            </button>
          )}
        </div>
      </main>
    </>
  );
}
